import { datadogRum } from '@datadog/browser-rum';
import { createNanoEvents, Emitter } from 'app/nanoevents';
import { checkEnroll, generateJWT, validate } from 'app/pages/frames/http/threeDS';
import {
  CardinalEvents,
  CardinalPaymentBrand,
  CybersourceResponseCodes,
  TwoFAEvents,
} from 'app/pages/frames/lib/3DS/constants';
import { getCardinalInitialData, getJWTCreateData } from 'app/pages/frames/lib/3DS/helpers';
import { PreAuth } from 'app/pages/frames/lib/3DS/interface';
import { checkoutRequestType } from 'app/util/constants';
import { apiAmountToCurrencyString } from 'app/util/currency';
import { CheckoutRequest } from 'common/interface';

const getCardinal = () => {
  const cardinal = (window as any).Cardinal;
  if (!cardinal) {
    throw { source: 'Cardinal is not available', data: null };
  }
  return cardinal;
};

export const startSetupCardinal = (jwt: string) => {
  const cardinal = getCardinal();
  cardinal.configure({ logging: { level: 'off' } });
  cardinal.setup('init', { jwt });
  return cardinal;
};

const createAdditionalOrderData = (preAuth: PreAuth, authorizationTfa: any) => {
  const {
    cavv,
    eci,
    veresEnrolled,
    xID,
    transactionId,
    specificationVersion,
    reasonCode,
    directoryServerTransactionId,
  } = authorizationTfa.objectResponse;

  return {
    checkoutAffiliation: preAuth.checkoutAffiliation,
    TwoFactorAuthentication: {
      cavv,
      eci,
      xid: xID,
      status: veresEnrolled,
      transactionId,
      version: specificationVersion,
      reasonCode,
      directoryServerTransactionId,
    },
  };
};

const handleSetupComplete = async (cardinal: any, preAuth: PreAuth, transactionData: any, emitter: Emitter) => {
  try {
    const checkEnrollData = await checkEnroll({ data: transactionData });

    const {
      objectResponse: { reasonCode = null, acsURL = null, authenticationTransactionId = null, paReq = null } = {},
    } = checkEnrollData;

    if (reasonCode === null) {
      emitter.emit(TwoFAEvents.ERROR, { source: 'handleSetupComplete reasonCode === null', data: checkEnrollData });
      return;
    }

    if (reasonCode !== CybersourceResponseCodes.DAUTHENTICATE || reasonCode === CybersourceResponseCodes.SOK) {
      emitter.emit(TwoFAEvents.CREATE_ORDER, createAdditionalOrderData(preAuth, checkEnrollData));
      return;
    }

    if (reasonCode === CybersourceResponseCodes.DAUTHENTICATE) {
      if (acsURL === null || paReq === null || authenticationTransactionId === null) {
        emitter.emit(TwoFAEvents.ERROR, {
          source: 'handleSetupComplete: args of object response are incomplete',
          data: checkEnrollData,
        });
        return;
      }
      cardinal.continue(
        CardinalPaymentBrand.CCA,
        { AcsUrl: acsURL, Payload: paReq },
        { OrderDetails: { TransactionId: authenticationTransactionId } },
      );
      return;
    }

    if (reasonCode !== CybersourceResponseCodes.SOK) {
      emitter.emit(TwoFAEvents.ERROR, { source: 'error 3ds', data: checkEnrollData });
      return;
    }
  } catch (e) {
    emitter.emit(TwoFAEvents.ERROR, { source: 'handleSetupComplete: Unexpected error occurred', data: e });
  }
};

const handlePaymentValidated = async (
  validationResult: any,
  cardToValidate: any,
  preAuth: PreAuth,
  emitter: Emitter,
) => {
  try {
    const { Payment } = validationResult;
    cardToValidate.authenticationTransactionID = Payment.ProcessorTransactionId;
    const validateData = await validate({ data: cardToValidate });
    emitter.emit(TwoFAEvents.CREATE_ORDER, createAdditionalOrderData(preAuth, validateData));
  } catch (e) {
    emitter.emit(TwoFAEvents.ERROR, { source: 'handlePaymentValidated: Unexpected error occurred', data: e });
  }
};

export const use3DSFlow = () => {
  const execute = async (
    checkoutRequest: CheckoutRequest,
    preAuth: PreAuth,
    formCardData: any,
    customerInfo: any,
    entityName?: string,
  ): Promise<any> => {
    const localEmitter = createNanoEvents();
    let localEmitterOnError: any, localEmitterOnCreate: any;
    try {
      const { checkoutAffiliation } = preAuth;
      const { amount, id, liveMode, type } = checkoutRequest;
      const cardinal = getCardinal();
      const cardinalInitialData = getCardinalInitialData(
        checkoutAffiliation.affiliation,
        apiAmountToCurrencyString(amount ?? 0),
        customerInfo,
        id,
        formCardData,
        type === checkoutRequestType.paymentLink ? liveMode : undefined,
        entityName,
      );
      const {
        cardToValidate: {
          cardModel: { cardAccountNumber },
        },
      } = cardinalInitialData;

      return await new Promise((resolve, reject) => {
        localEmitterOnError = localEmitter.on(TwoFAEvents.ERROR, (error) => reject(error));
        localEmitterOnCreate = localEmitter.on(TwoFAEvents.CREATE_ORDER, (additionalOrderData) =>
          resolve(additionalOrderData),
        );
        cardinal.trigger('bin.process', cardAccountNumber);
        cardinal.on(CardinalEvents.VALIDATED, (validationResult: any) =>
          handlePaymentValidated(validationResult, cardinalInitialData.cardToValidate, preAuth, localEmitter),
        );
        handleSetupComplete(cardinal, preAuth, cardinalInitialData.transactionData, localEmitter);
      });
    } catch (e) {
      datadogRum.addError(e, { source: 'use3DSFlow' });
      throw e;
    } finally {
      dispose();
      localEmitterOnError && localEmitterOnError();
      localEmitterOnCreate && localEmitterOnCreate();
    }
  };

  const initCardinal = async (checkoutRequest: CheckoutRequest) => {
    const { amount, id, orderTemplate, name, liveMode, type } = checkoutRequest;
    const jwt = await generateJWT({
      data: getJWTCreateData({
        amount: amount.toString() ?? '0',
        id,
        customerName: orderTemplate?.customerInfo?.name ?? '',
        name,
        liveMode: type === checkoutRequestType.paymentLink ? liveMode : undefined,
      }),
    });
    startSetupCardinal(jwt);
  };

  const dispose = () => {
    try {
      const cardinal = getCardinal();
      cardinal.off(CardinalEvents.SETUP_COMPLETE);
      cardinal.off(CardinalEvents.VALIDATED);
    } catch (e) {
      console.warn(e);
    }
  };

  return { dispose, execute, initCardinal };
};
